import type { TemplateUIObject } from "@services/storage/storeTemplate";
import CancellationTokenStore from "@services/tokens";
// ----------------------------------------------------------------------------
// Author:  Adam Rzymski
// Project: Booking System
// Name:    ContactUtils
// Date:    28/03/2022
// Version: 1.0.0
// Type:    Class
// Purpose: Provides a class for processing contacts and triggers UI actions
// ----------------------------------------------------------------------------
//
// Language: typescript
// Path: ClientApp\src\utils\contact\index.ts
//
// ----------------------------------------------------------------------------
// Description:
// This class provides various static utility methods for processing contacts,
// and triggering UI actions. To use simply import ContactsUtils from this file.
// Because this class is a static class, it is not required to instantiate it.
// Methods use interfaces to define the input and output parameters.
// Interfaces are defined in model/index.ts
// Some of the methods are async, and therefore return promises.
// Some of the async methods are using the API, and also return promises.
// Methods which may compute a result, sometimes modify instances of the
// other objects passed to them. These objects are passed by reference, and are
// therefore modified by the method. This is to modify application state.
// ----------------------------------------------------------------------------
// Imports:
// ----------------------------------------------------------------------------
import { logger } from "@services/workers/helpers";
import { state } from "../../interfaces/interfaces";
import { StringProcessor } from "../stringProcessor";
import type {
	ContactFormFields,
	ContactFromAPI,
	ContactToContactString,
	ConvertContactFormFieldsToContact,
	MapContactToFormContact,
	MergedContacts,
	showContactModal,
} from "./model";
// ----------------------------------------------------------------------------
// Class
// ----------------------------------------------------------------------------
export class ContactUtils {
	// Public Properties
	// --------------------------------------------------------------------------
	public static cancellationTokens: CancellationTokenStore =
		new CancellationTokenStore();
	public static nameSpace: string = "ContactUtils";

	// ShowContactModal:
	// --------------------------------------------------------------------------
	// Shows the contact modal by setting ui modal to true
	// --------------------------------------------------------------------------
	public static showContactModal: showContactModal = ({ ui, prefix }) => {
		if (
			prefix !== "LandlordContact" &&
			prefix !== "RelatedContact" &&
			prefix !== "PropertyContact"
		) {
			const fixedPrefix = prefix.replace("Contact", "");
			ui[`${fixedPrefix}ContactModalOpen` as keyof TemplateUIObject] =
				true as never;
		}
	};
	// ### END: ShowContactModal

	// MergeContacts:
	// --------------------------------------------------------------------------
	// Merges the contacts and returns the merged contacts array.
	// Combines contacts which are already stored for other forms in the booking,
	// with the new contact resulting from searching for the contact.
	// Puts existing contact in the first position of the array.
	// If existing contacts replicates any of the other accounts contacts,
	// the existing contact is removed from the array and accounts are merged.
	// Existing contacts indicate the type of the account related to. ie. Landlord, Tenant, etc.
	// --------------------------------------------------------------------------
	public static mergeContacts: MergedContacts = ({ ui, prefix, temp }) => {
		// create existing contacts array
		const existingContacts: ContactFromAPI[] = [];
		if (prefix === "RelatedContact") return existingContacts;

		// log the contacts
		logger.debug(
			this.nameSpace + "->" + this.mergeContacts.name,
			`Merge Contacts: ${prefix}Contacts`,
		);

		// create current search contacts array
		// map the current search contacts and add scope as "Search"
		// const currentSearchContacts = temp[`${prefix}Contacts`].map((a: Contact) => ({
		//   ...a,
		//   scope: "Search",
		// })) as Contact[];

		// iterate through the accountsList, if tenant lives in property and current account is "Landlord", skip the account
		for (const account of temp.ContactAccountsListAllowedToMerge) {
			if (!state[`${account}Valid`]) continue;
			// otherwise push the account contact to the temp contact array if not empty
			// create the contact object for account
			const accountContact = this.convertContactFormFieldsToContact({
				ui,
				scope: account,
			});
			// if the account contact is not empty, push it to the existing contacts array
			if (accountContact) {
				// check if the account contact is already in the existing contacts array
				const existingContact = existingContacts.find(
					(a: ContactFromAPI) =>
						a.contactString === accountContact.contactString,
				);
				// if the account contact is in the existing contacts array,
				if (existingContact) {
					// merge the scope of the account contact with the existing contact
					// replace existing contact in the existing contacts array with the account contact and remove "Stored: " word from the scope
					existingContacts[existingContacts.indexOf(existingContact)] = {
						...existingContact,
						scope: `${existingContact.scope}, ${accountContact.scope?.replace("Stored: ", "")}`,
					};
				} else {
					// otherwise, the account contact is not in the existing contacts array, push it to the existing contacts array
					existingContacts.push(accountContact);
				}
			}
		}

		// create the merged contacts array using the current contacts and temp contacts with spread operator
		const mergedContacts = [...existingContacts];
		// return the return contact array
		return mergedContacts;
	};
	// ### END: MergeContacts

	// ConvertContactFormFieldsToContact:
	// --------------------------------------------------------------------------
	// Converts the fields from contact form to Contact object.
	// --------------------------------------------------------------------------
	public static convertContactFormFieldsToContact: ConvertContactFormFieldsToContact =
		({ ui, scope }) => {
			// create the Contact Form Fields
			const contactFormFields: ContactFormFields = {};
			// array of contact form fields that are required
			const requiredContactFields = [
				"Title",
				"FirstName",
				"LastName",
				"Email",
				"Mobile",
				"Landline",
			];

			// loop through the required contact fields
			for (const fieldName of requiredContactFields) {
				const fieldValue =
					ui[
						`${scope}${fieldName as "Title" | "FirstName" | "LastName" | "Email" | "Mobile" | "Landline"}`
					];
				// if the field is not empty
				if (fieldValue) {
					contactFormFields[fieldName as keyof ContactFormFields] = fieldValue;
				}
			}
			// if the contact form firstName is empty return null
			if (!contactFormFields?.FirstName) return null;

			// destruct the contact form fields into the contact fields
			const {
				FirstName: firstName,
				LastName: lastName,
				Title: title,
				Email: email,
				Mobile: mobile,
				Landline: landline,
			} = contactFormFields;

			// create contact object
			const contact: ContactFromAPI = {
				contactString: "",
				firstName,
				lastName,
				mobile,
				landline,
				email,
				title,
			};

			// create empty contact object
			const returnContact: ContactFromAPI = {};

			// loop through the contact object and add the values to the return contact object
			// if the value is not empty
			for (const fieldName in contact) {
				const fieldValue = contact[fieldName as keyof ContactFromAPI];
				// if the field is not empty
				if (fieldValue) {
					// set the return contact object to the trimmed contact object value
					(returnContact[fieldName as keyof ContactFromAPI] as any) =
						String(fieldValue).trim();
				}
			}
			// if the return contact object is not empty
			if (Object.keys(contactFormFields).length > 0) {
				// create the contact string from the return contact object and add it to the return contact object
				returnContact.contactString = this.contactToContactString({
					contact: returnContact,
				});

				// add scope to the return contact object
				returnContact.scope = `Stored: ${StringProcessor.camelToWords(scope)}`;
			}

			// return the return contact object
			return returnContact;
		};
	// ### END: ConvertAddressFormFieldsToAddress

	// ContactToContactString:
	// --------------------------------------------------------------------------
	// Converts the contact to contact string, adds the county
	// --------------------------------------------------------------------------
	public static contactToContactString: ContactToContactString = ({
		contact,
	}) => {
		const contactString = `${contact.title ? `${contact.title},` : ""}${
			contact.firstName ? `${contact.firstName},` : ""
		}${contact.lastName ? ` ${contact.lastName},` : ""}${contact.email ? ` ${contact.email}` : ""}${
			contact.mobile
				? ` ${contact.mobile}${contact.landline ? ` ${contact.landline}` : ""}`
				: ""
		}`.trim();
		return contactString;
	};
	// ### END: ContactToContactString

	// MapContactToContactFormFields:
	// --------------------------------------------------------------------------
	// Maps the contact to contact form fields.
	// --------------------------------------------------------------------------
	public static mapContactResourceToFormContact: MapContactToFormContact = (
		contactResource,
	) => {
		const {
			firstName: FirstName,
			lastName: LastName,
			title: Title,
			email: Email,
			mobile: Mobile,
			landline: Landline,
		} = contactResource;

		const fields: ContactFormFields = {
			FirstName,
			LastName,
			Title,
			Email,
			Mobile,
			Landline,
		};
		const formContact: ContactFormFields = Object.keys(fields).reduce(
			(acc, key) => {
				if (fields[key as keyof ContactFormFields]) {
					acc[key as keyof ContactFormFields] =
						fields[key as keyof ContactFormFields];
				} else {
					acc[key as keyof ContactFormFields] = "";
				}
				return acc;
			},
			{} as any,
		);
		return formContact;
	};
	// --------------------------------------------------------------------------
	// Private methods
	// --------------------------------------------------------------------------
}

declare global {
	interface Window {
		ContactUtils: ContactUtils;
	}
}

window.ContactUtils = ContactUtils;

export default ContactUtils;
